import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import AdminNavbar from "../components/Navbar/AdminNavbar";
// AdminSidebar removed — admin uses full-width layout
// import AdminSidebar from "../components/Sidebar/AdminSidebar";
import Sidebar from "../components/Sidebar/Sidebar";
import MobileBottomNav from "../components/Sidebar/MobileBottomNav";
import ImpersonationBanner from "../components/ImpersonationBanner/ImpersonationBanner";
import { getRole } from "../components/Apicall";
import { useImpersonation } from "../components/context/ImpersonationContext";
import FloatingVideoPlayer from "../components/Help/FloatingVideoPlayer";
import PageTour from "../components/Help/PageTour";
import { CallProvider } from "../components/context/CallProvider";
import RotateHint from "../components/RotateHint/RotateHint";
import useViewport from "../hooks/useViewport";
import { MobileNavProvider } from "../components/MobileNav/MobileNavContext";
import SidebarV2, { RAIL_W } from "../components/NavV2/SidebarV2";
import useNavPrefs, { applyNavVersionFromSearch } from "../components/NavV2/navPrefs";
import "./Layout.css";

const BANNER_HEIGHT = 44; // px — must match ImpersonationBanner.css height
// Phone bottom tab bar: keep in step with --bottom-bar-h in Layout.css.
const BOTTOM_BAR_HEIGHT = 64;

const Layout = () => {
  const [userRole, setUserRole] = useState(() => getRole());
  const [vendorData, setVendorData] = useState();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // New menu (module rail + page panel): reports how wide it is right now.
  const [navV2Width, setNavV2Width] = useState(RAIL_W);
  const { version: navVersion } = useNavPrefs();
  const useNewMenu = navVersion === "v2";
  // One viewport source for the whole shell (see hooks/useViewport.js). The same
  // value is written to data-shell below, so the CSS and JS can never disagree
  // about which layout a given iPad width gets.
  const { isPhone, isTablet, shell } = useViewport();
  const { isImpersonating } = useImpersonation();

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "userRole") setUserRole(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Every persona except Super Admin (1) uses the vendor-side shell
  // (Sidebar + Navbar + MobileBottomNav) - Admin (2) and Crew (5) were
  // missing here, which left them with no Sidebar/Navbar at all (silently
  // fell into the Super Admin master-console branch instead). String(...)
  // guards against a token whose role claim came back as a number instead
  // of a string (some login paths sign the raw DB value, which mysql2
  // returns numeric for this column) - this check must not depend on which
  // login method produced the token.
  const isVendor = ["2", "3", "5", "6"].includes(String(userRole));
  const location = useLocation();

  // ?nav=v2 / ?nav=v1 on any page switches the menu (saved on this browser).
  useEffect(() => {
    applyNavVersionFromSearch(location.search);
  }, [location.search]);

  const noSidebarRoutes = ["/industry", "/service-location"];
  const hideSidebar = noSidebarRoutes.includes(location.pathname);

  // Sidebar only for vendors — admin uses full-width layout
  const desktopSidebar = !isVendor
    ? null
    : useNewMenu
    ? <SidebarV2 vendorData={vendorData} overlay={isTablet} onWidthChange={setNavV2Width} />
    : <Sidebar vendorData={vendorData} onCollapseChange={setSidebarCollapsed} overlay={isTablet} />;

  const renderNavbar = isVendor ? (
    <Navbar vendorDataPass={(data) => setVendorData(data)} />
  ) : (
    <AdminNavbar />
  );

  // Admin has no sidebar → always full-width; vendor uses sidebar offset.
  // On a tablet the sidebar is always a 72px rail and expanding it overlays the
  // content (with a scrim) instead of pushing it, so a 744-1024px canvas is
  // never squeezed by a 160px push.
  const contentMargin = !isVendor || isPhone || hideSidebar
    ? 0
    : useNewMenu
    ? `${navV2Width}px`
    : isTablet || sidebarCollapsed
    ? "72px"
    : "160px";
  const bannerOffset = isImpersonating ? BANNER_HEIGHT : 0;
  const showBottomBar = isPhone && isVendor && !hideSidebar;

  const shellContent = (
    <MobileNavProvider>
    {/* --banner-h / --navbar-h are consumed by Layout.css (navbar top, content
        padding-top, sidebar top) and by the navbar panels - no injected <style>. */}
    <div className="layout-container" data-shell={shell} style={{ "--banner-h": `${bannerOffset}px` }}>

      {/* Admin impersonation banner — renders at very top, z-index 2000 */}
      <ImpersonationBanner />

      {renderNavbar}

      <div className="d-flex">
        {/* Sidebar — vendor only, hidden on phone and for admin */}
        {isVendor && !isPhone && !hideSidebar && (
          <div className="sidebar-wrapper">{desktopSidebar}</div>
        )}

        {/* Main content */}
        <main
          className="content-area"
          style={{
            marginLeft: contentMargin,
            transition: "margin-left 0.25s ease",
            paddingBottom: showBottomBar
              ? `calc(${BOTTOM_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`
              : "16px",
          }}
        >
          <Outlet />
        </main>
      </div>

      {/* Phone bottom tab bar */}
      {showBottomBar && <MobileBottomNav vendorData={vendorData} newMenu={useNewMenu} />}

      {/* Portrait hint on dense screens - dismissible, never blocks */}
      <RotateHint />

      {/* Floating video tutorial player — persists across route changes */}
      <FloatingVideoPlayer />

      {/* Guided page tour — reacts to current route */}
      <PageTour />
    </div>
    </MobileNavProvider>
  );

  // WhatsApp call alerts (ring, popup, answer) work on every screen that has
  // the navbar — but only for the vendor (3) and their support team (6), the
  // people who actually take customer calls.
  return ["3", "6"].includes(String(userRole)) ? <CallProvider>{shellContent}</CallProvider> : shellContent;
};

export default Layout;
